import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ButtonStyleProps } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Dialog, DialogActionClose, DialogActionPrimary, DialogActions, DialogContent, DialogProps, DialogTrigger } from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { RadioGroup, RadioGroupProps } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { usePaymentMethodRemove } from '@sola/my-sdk/payment-methods/hooks.ts';
import { PaymentTypeEnum } from '@sola/schema/dotcom/proxy-api/paymentMethods.ts';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.ts';
import { GraphQLCommitment, GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { CommitmentAvatarGroup } from '../../molecules/commitment-avatar-group/commitment-avatar-group.tsx';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { createPaymentMethodSuccessAlert, createSupportAlert, removePaymentMethodFailureAlert, removePaymentMethodPartialSuccessAlert } from '../../utils/alerts.tsx';
import { getErrorTranslationKeys } from '../../utils/errors.ts';
const PaymentMethodLabel = t0 => {
  const $ = _c(9);
  const {
    paymentMethod,
    ignoreNickname: t1,
    isBold: t2
  } = t0;
  const ignoreNickname = t1 === undefined ? false : t1;
  const isBold = t2 === undefined ? false : t2;
  const {
    accountNickname,
    paymentType,
    creditCard,
    ach
  } = paymentMethod;
  const showNickname = !ignoreNickname && accountNickname;
  const t3 = showNickname ? "my:payments.methodLabels.withNickname" : "my:payments.methodLabels.default";
  const t4 = paymentType === PaymentTypeEnum.CreditCard ? creditCard?.cardType : "Checking";
  const t5 = paymentType === PaymentTypeEnum.CreditCard ? creditCard?.lastFour : ach?.lastFourOfAccountNumber;
  let t6;
  if ($[0] !== accountNickname || $[1] !== t4 || $[2] !== t5) {
    t6 = {
      nickname: accountNickname,
      type: t4,
      lastFour: t5
    };
    $[0] = accountNickname;
    $[1] = t4;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== t3 || $[5] !== t6) {
    t7 = <IntlBlock i18nKey={t3} values={t6} />;
    $[4] = t3;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const label = t7;
  if (isBold && !showNickname) {
    let t8;
    if ($[7] !== label) {
      t8 = <strong>{label}</strong>;
      $[7] = label;
      $[8] = t8;
    } else {
      t8 = $[8];
    }
    return t8;
  }
  return label;
};
const PaymentMethodSupportingText = t0 => {
  const $ = _c(2);
  const {
    paymentMethod
  } = t0;
  let t1;
  if ($[0] !== paymentMethod) {
    t1 = <IntlBlock i18nKey="my:deletePaymentMethod.removeMethodDialog.supportingTextRecurring" components={{
      PaymentLabel: <PaymentMethodLabel paymentMethod={paymentMethod} isBold={true} />
    }} />;
    $[0] = paymentMethod;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const mapPaymentMethodsToRadioGroupItem = (paymentMethods?: GraphQLSupporterPaymentMethod[]): RadioGroupProps['items'] => {
  if (!paymentMethods?.length) {
    return undefined;
  }
  return paymentMethods.map(paymentMethod => {
    if (!paymentMethod.id || !paymentMethod.ach && !paymentMethod.creditCard) {
      return null;
    }
    return {
      value: paymentMethod.id,
      element: <span className="flex items-center gap-comfy-xs">
            <PaymentMethodLabel paymentMethod={paymentMethod} ignoreNickname />
            {paymentMethod.accountNickname && <Badge label={paymentMethod.accountNickname} size="sm" />}
          </span>
    };
  }).filter(item => !!item);
};
export type PaymentMethodRemoveDialogProps = Omit<DialogProps, 'modal'> & {
  openTrigger: React.ReactElement<ButtonStyleProps>;
  recurringCommitments: GraphQLCommitment[];
  selectedPaymentMethod: GraphQLSupporterPaymentMethod;
  alternatePaymentMethods: GraphQLSupporterPaymentMethod[];
};
export const PaymentMethodRemoveDialog: React.FC<PaymentMethodRemoveDialogProps> = ({
  openTrigger,
  selectedPaymentMethod,
  recurringCommitments,
  alternatePaymentMethods
}) => {
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [newPaymentMethodId, setNewPaymentMethodId] = useState<GraphQLSupporterPaymentMethod['id'] | null>(null);
  const {
    mutateAsync,
    isPending
  } = usePaymentMethodRemove(selectedPaymentMethod?.id || undefined);
  const {
    addAlerts,
    clearAlerts
  } = useLocalAlerts();
  const {
    t
  } = useTranslation('my');
  const handleRemove = async () => {
    if (!selectedPaymentMethod.id) {
      return;
    }
    clearAlerts();
    try {
      const {
        success,
        isPartialSuccess
      } = await mutateAsync(newPaymentMethodId || undefined);
      if (success && !isPartialSuccess) {
        addAlerts(createPaymentMethodSuccessAlert(selectedPaymentMethod, 'deletePaymentMethod.success.action'));
      }
      if (!success && isPartialSuccess) {
        addAlerts(removePaymentMethodPartialSuccessAlert());
      }
      if (!success && !isPartialSuccess) {
        addAlerts(removePaymentMethodFailureAlert());
      }
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(SupporterErrorTypeEnum.PaymentMethodDelete, errors);
      addAlerts(translationKeys.map(translationKey => {
        return createSupportAlert(translationKey);
      }));
    }

    // TODO: Make sure dialog can close when form submits
    setIsRemoveDialogOpen(false);
  };
  return <Dialog open={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent size="md" icon="info-circle" purpose="negative" title={t('deletePaymentMethod.removeMethodDialog.title')} supportingText={recurringCommitments.length ? <PaymentMethodSupportingText paymentMethod={selectedPaymentMethod} /> : t('deletePaymentMethod.removeMethodDialog.supportingText')}>
        {!!recurringCommitments.length && <>
            <div className="body-neutral-potent">
              <RadioGroup name="payment-method-select" loop={false} value={newPaymentMethodId} onChange={e => {
            setNewPaymentMethodId(e.target.value);
          }} items={mapPaymentMethodsToRadioGroupItem(alternatePaymentMethods)} />
            </div>
            <Divider purpose="neutral" className="my-comfy-xl" />
            <div className="flex flex-col gap-comfy-sm">
              <p className="body-neutral-mild">
                <IntlBlock i18nKey="my:deletePaymentMethod.removeMethodDialog.newPaymentMethodNote" />
              </p>
              <CommitmentAvatarGroup commitments={recurringCommitments} layout="stacked" tooltipMessage={t('payments.methodCommitmentsTooltip', {
            count: recurringCommitments.length
          })} usePortal={false} />
            </div>
          </>}
        <DialogActions>
          {recurringCommitments.length ? <>
              <DialogActionClose>
                {t('deletePaymentMethod.removeMethodDialog.cancel')}
              </DialogActionClose>
              <DialogActionPrimary purpose="negative" isBusy={isPending} disabled={!newPaymentMethodId} onClick={handleRemove}>
                {t('deletePaymentMethod.removeMethodDialog.saveAndRemove')}
              </DialogActionPrimary>
            </> : <>
              <DialogActionClose>
                {t('deletePaymentMethod.removeMethodDialog.cancel')}
              </DialogActionClose>
              <DialogActionPrimary purpose="negative" isBusy={isPending} onClick={handleRemove}>
                {t('deletePaymentMethod.removeMethodDialog.remove')}
              </DialogActionPrimary>
            </>}
        </DialogActions>
      </DialogContent>
    </Dialog>;
};